import { useEffect, useRef, useState } from 'react';
import { zIndex } from 'helper/Style';
import OperationFilter from 'component/part/OperationFilter';
import Svg from 'component/part/Svg';

export type SelectItem = Required<{ [key in string]: any }>;

export type SelectProps<T extends SelectItem> = Readonly<{
  list: T[];
  labelKey: keyof T;
  value?: T;
  blankLabel?: string;
  disabled?: boolean;
  onChange: (value?: T) => void | Promise<void>;
  className?: string;
}>;

const style = {
  selectArea: {
    position: 'relative' as const,
  },
  select: (active: boolean, disabled?: boolean) => ({
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    borderBottom: 'solid 0.1rem #ccc',
    fontSize: '1.6rem',
    cursor: 'pointer',
    ...(active ? { zIndex: zIndex.filter + 1 } : {}),
    ...(disabled ? {
      color: '#cccccc',
    } : {
      ':hover': {
        backgroundColor: '#eeeeff',
      },
    }),
    icon: {
      display: 'inlineBlock',
      width: '1.6rem',
      fill: '#888888',
      marginLeft: 'auto',
    },
  }),
  candidateList: {
    position: 'absolute' as const,
    top: '2.5rem',
    width: 'fit-content',
    height: '0',
    maxHeight: '50rem',
    overflowY: 'hidden' as const,
    backgroundColor: '#ffffffee',
    borderRadius: '0.3rem',
    fontSize: '1.6rem',
    '&[data-active="1"]': {
      height: 'initial',
      overflowY: 'scroll' as const,
      border: 'solid 0.1rem #cccccc',
      zIndex: zIndex.filter + 1,
    },
    '& div': {
      padding: '0.2rem 2rem 0.2rem 0',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      '&[data-selected="1"]': {
        backgroundColor: '#ccccff',
      },
      ':hover': {
        backgroundColor: '#eeeeff',
      },
    },
  },
};

const Select = <T extends SelectItem>(props: SelectProps<T>) => {
  const [active, setActive] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const candidateListRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  const onChange = async (value?: T) => {
    await props.onChange(value);
    setActive(false);
  };

  useEffect(() => {
    const { width: candidateListWidth = 0 } = candidateListRef.current?.getBoundingClientRect() || {};
    if (selectRef.current && candidateListWidth) {
      selectRef.current.style.width = `${candidateListWidth}px`;
    }
  }, [selectRef, candidateListRef, props.list]);

  useEffect(() => {
    if (active && candidateListRef.current) {
      const selectedItemOffset = selectedRef.current?.offsetTop || 0;
      candidateListRef.current.style.top = `${selectedItemOffset * -1}px`;
    }
  }, [candidateListRef, active]);

  const itemList = Array(props.list.length).fill(null).map((_, index) => ({
    value: props.list[index],
    label: props.list[index][props.labelKey],
  }));

  const selectedItem = itemList.find((v) => v.value === props.value);
  const iconColor = props.disabled ? '#cccccc' : '';

  return <div className={props.className}>
    <OperationFilter display={active} onHide={() => setActive(false)} />
    <div data-part='area' css={style.selectArea}>
      <div
        onClick={() => props.disabled || setActive(true)}
        ref={selectRef}
        data-part='select'
        css={style.select(active, props.disabled)}
      >
        {selectedItem ? selectedItem.label : (props.blankLabel || '　')}
        <Svg src='/image/icon/caret-down-fill.svg' color={iconColor} />
      </div>
      <div
        ref={candidateListRef}
        data-active={active ? '1' : '0'}
        data-part='options'
        css={style.candidateList}
      >
        {props.blankLabel && (
          <div
            onClick={() => onChange()}
            ref={!props.value ? selectedRef : undefined}
            key='blank'
            {...(!props.value ? { 'data-selected': '1' } : {})}
          >
            {props.blankLabel}
          </div>
        )}
        {itemList.map(({ value, label }, index) => (
          <div
            onClick={() => onChange(value)}
            ref={value === props.value ? selectedRef : undefined}
            key={index}
            {...(value === props.value ? { 'data-selected': '1' } : {})}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  </div>;
};

export default Select;